import { useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigationStack } from '../../../../context/NavigationStackContext';
import { useLazyLoad } from '../../../../hooks/use-lazy-load';
import { useRestrictions } from '../../../../hooks/use-restrictions';
import { useDebounce } from '../../../../hooks/useDebounce';
import { useGetAppFeatures } from '../../../../redux/api/appConfigApi';
import { useLazyGetUserTrainingsLazy } from '../../../../redux/api/trainingsAPI';
import { AppFeatureKey } from '../../../../shared/constants';
import { Paths } from '../../../../shared/paths';

interface IUseMyTrainingsPageParams {
  setPlanDialogOpen: (open: boolean) => void;
  onStartEmptyTrainingClick: () => void;
}

const SEARCH_DEBOUNCE_MS = 1000;

export function useMyTrainingsPage({
  setPlanDialogOpen,
  onStartEmptyTrainingClick,
}: IUseMyTrainingsPageParams) {
  const { navigate } = useNavigationStack();
  const [filter, setFilter] = useState('');
  const [loaded, setLoaded] = useState(false);
  const { data: features } = useGetAppFeatures();
  const isAiGenerationActive = features?.find(
    (f) => f.key === AppFeatureKey.aiGeneration,
  )?.isActive;
  const {
    items: trainings,
    footerRef,
    refetchAll,
    resetAll,
    retryLastRequest,
    isFetching,
    error,
  } = useLazyLoad(useLazyGetUserTrainingsLazy, { searchValue: filter });
  const {
    isShowAddButton,
    isShowPlanDialogOnClick,
    restrictionsPermissionsReady,
  } = useRestrictions();

  const callback = useCallback(() => {
    resetAll(true);
  }, [resetAll]);

  const [debouncedSearch] = useDebounce<(a: any) => void>(
    callback,
    SEARCH_DEBOUNCE_MS,
  );

  useEffect(() => {
    if (loaded) {
      debouncedSearch(filter);
    } else {
      setLoaded(true);
    }
  }, [filter]);

  function onAddNewTrainingClicked() {
    navigate(Paths.trainings.toNew);
  }

  function onStartClick() {
    if (isShowPlanDialogOnClick?.startTraining) {
      setPlanDialogOpen(true);
    } else {
      onStartEmptyTrainingClick();
    }
  }

  const onGenerateTrainingClick = useCallback(() => {
    if (isShowPlanDialogOnClick?.generateTraining) {
      setPlanDialogOpen(true);
    } else {
      navigate(Paths.trainings.toGenerate);
    }
  }, [isShowPlanDialogOnClick?.generateTraining, navigate, setPlanDialogOpen]);

  const generateTrainingHandler = useMemo(() => {
    if (!isAiGenerationActive || !restrictionsPermissionsReady) {
      return undefined;
    }
    return onGenerateTrainingClick;
  }, [
    isAiGenerationActive,
    restrictionsPermissionsReady,
    onGenerateTrainingClick,
  ]);

  return {
    trainings,
    filter,
    setFilter,
    isLoading: Boolean(
      isFetching && (trainings === null || trainings.length === 0),
    ),
    onAddNewTrainingClicked,
    onGenerateTrainingClick: generateTrainingHandler,
    onStartClick,
    isAddButtonVisible: isShowAddButton?.training,
    footerRef,
    isFetching,
    error,
    refetchAll,
    resetAll,
    retryLastRequest,
  };
}
